/* eslint-disable @typescript-eslint/no-explicit-any -- createLoadingDataReducer used in 50+ reducers */
import { CREATE_ACTIVITY } from '@common/constants/webSockets'
import isEqual from 'lodash/isEqual'
import { Action } from 'redux'
import { all, put, spawn } from 'redux-saga/effects'
import { logError } from '../redux/logger/actions'
import { CREATE_PARTICIPATION_WIRE_SUCCESS } from '../redux/participant/actions'
import { CREATE_PROSPECT_TERMS_SUCCESS } from '../redux/prospect/actions'
import { ILoadingData, IPromiseAction } from '../redux/types'

const ADD_TO_TOP_ACTIONS = [
  CREATE_ACTIVITY,
  CREATE_PARTICIPATION_WIRE_SUCCESS,
  CREATE_PROSPECT_TERMS_SUCCESS,
]

/**
 * Did an awaited promise-action fail? apiMiddleware RESOLVES failed requests with
 * `{ ...rest, error, type: FAILURE }` rather than rejecting, so `await someAction()`
 * succeeds even when the server returned an error. Any caller that closes a modal,
 * refetches, or otherwise treats the write as done must check this first, or a
 * failure looks exactly like a success.
 */
export const hasRequestFailed = (result: unknown): boolean =>
  !!(result as { error?: unknown } | undefined)?.error

/** Awaits each promise-action before starting the next, stopping at the first failure. True if all landed. */
export const runRequestsInSequence = async (
  requests: (() => Promise<unknown>)[],
): Promise<boolean> => {
  for (const request of requests) {
    if (hasRequestFailed(await request())) {
      return false
    }
  }
  return true
}

/** The server's message for a failed promise-action (or `fallback`), null when it succeeded. */
export const requestFailureMessage = (result: unknown, fallback: string): string | null => {
  if (!hasRequestFailed(result)) {
    return null
  }
  return (result as { error?: { message?: string } } | undefined)?.error?.message || fallback
}

/**
 * The stored photo from a dispatched photo mutation — throwing if the mutation actually failed.
 *
 * The enforcement of the contract above, for the photo endpoints. Because apiMiddleware resolves
 * rather than rejects, `await updatePhoto(...)` looks successful whether the server accepted the
 * upload or refused it, and a caller that only reads `.data.photo` gets `undefined` from a failure
 * and cannot tell it apart from "saved, but the server echoed nothing back".
 *
 * That is not a hypothetical: eight of the nine photo call sites made exactly that mistake, so a
 * rejected upload (a non-image, or one over the size limit) showed the user their own cropped
 * image as though it had saved, primed the shared cache so every avatar on the page adopted it,
 * and closed the dialog. ProfilePhotoEditor calls this on the raw result so no caller has to
 * remember the contract — see its IProps.onUpload.
 */
export const photoFromResult = (result: unknown, action: 'save' | 'remove'): string | null => {
  if (hasRequestFailed(result)) {
    throw new Error(action === 'save' ? 'Could not save that photo' : 'Could not remove that photo')
  }

  return (result as { data?: { photo?: string | null } } | undefined)?.data?.photo ?? null
}

const buildActionTypesRegExp = (actionTypes?: string[]): RegExp | null => {
  if (!actionTypes?.length) {
    return null
  }

  return new RegExp(
    `^(?:${[
      ...new Set(
        actionTypes
          .map((item) => item.replace(/_(request|success|failure)/gi, ''))
          .map((type) => [`${type}_REQUEST`, `${type}_SUCCESS`, `${type}_FAILURE`])
          .flat(),
      ),
    ].join('|')})`,
    'i',
  )
}

interface INestedItem {
  rows: INestedItem[]
  [key: string]: unknown
}

interface INestedUpdatePayload {
  data: INestedItem[]
  [key: string]: unknown
}

const updateNestedData = (
  data: INestedItem[],
  payload: INestedUpdatePayload,
  keys: string[],
  loadMore: boolean,
): INestedItem[] => {
  const [currentKey, ...remainingKeys] = keys

  return data.map((item) => {
    if (item[currentKey] === payload[currentKey]) {
      if (remainingKeys.length === 0) {
        return {
          ...item,
          rows: loadMore ? [...item.rows, ...payload.data] : payload.data,
        }
      }
      return {
        ...item,
        rows: updateNestedData(item.rows, payload, remainingKeys, loadMore),
      }
    }
    return item
  })
}

/** Opt-in list behaviour for paged tables. */
export interface ILoadingListOptions {
  /** Kept per replacement load; a `loadMore` page asked for under a different query is dropped. */
  pickQuery?: (params: Record<string, unknown>) => unknown
}

/**
 * The default `ILoadingData` bucket. Shared by `createLoadingDataReducer` and
 * `createKeyedLoadingDataReducer` so the two can't silently drift apart.
 */
const defaultLoadingDataState = <T>(): ILoadingData<T> => ({
  isLoading: true,
  isSaving: false,
  isSaved: false,
  data: null,
})

export const createLoadingDataReducer = <T = any>(
  loadingActionTypes: string[],
  savingActionTypes?: string[],
  withSaveActionLiveEdit?: boolean,
  resetActionTypes?: string[],
  withMergeOnUpdate?: boolean,
  customLiveEdit?: (state: ILoadingData<T>, action: Action) => ILoadingData<T>,
  initialState: ILoadingData<T> = defaultLoadingDataState<T>(),
  listOptions: ILoadingListOptions = {},
): ((state: ILoadingData<T> | undefined, action: any) => ILoadingData<T>) => {
  const loadingRegExp = buildActionTypesRegExp(loadingActionTypes)
  const savingRegExp = buildActionTypesRegExp(savingActionTypes)
  return (
    state = initialState,
    action: Action & {
      data: T & { id?: string; ids?: string[]; items?: Array<{ id: string }> }
      /** Stamped by apiMiddleware; see the supersede check below. */
      requestId?: number
      params: {
        loadMore?: boolean
        skipLoader?: boolean
        nestedRows?: { keys: string[] }
        customLiveEdit?: (state: ILoadingData<T>, action: Action) => ILoadingData<T>
      }
    },
  ) => {
    if (loadingRegExp && loadingRegExp.test(action.type)) {
      if (action.type.endsWith('_REQUEST')) {
        const isReplacement = !action.params?.loadMore && !action.params?.nestedRows
        return {
          ...state,
          ...(isReplacement &&
            listOptions.pickQuery && { query: listOptions.pickQuery(action.params ?? {}) }),
          /**
           * Only a replacement claims the newest id.
           *
           * The two sides have to agree about what supersedes what: an appending
           * request does not invalidate the load it is adding to, so letting it
           * advance this would discard a replacement still in flight — and since
           * a dropped reply never reaches the branch that clears `isLoading`, the
           * table would keep its old rows and spin forever.
           */
          latestRequestId:
            action.params?.loadMore || action.params?.nestedRows
              ? state.latestRequestId
              : action.requestId,
          isLoading:
            !action.params?.loadMore && !action.params?.skipLoader && !action.params?.nestedRows
              ? true
              : state.isLoading,
        }
      }

      /**
       * A reply that a later load has already overtaken.
       *
       * Responses come back out of order — a broad search matches far more rows
       * than the narrower one typed after it and can take longer to return — and
       * what the table owes the reader is the answer to what is in the box now,
       * not whichever answer landed last. Without this the slice settles on the
       * stale one, and because it is state rather than a flicker, it is still
       * there when the reader acts on it.
       *
       * Only replacements are dropped. `loadMore` and `nestedRows` append to what
       * is already held rather than supersede it, so discarding one would lose a
       * page instead of an outdated answer. The saving branch below is untouched
       * for the same reason: two writes dispatched together are both wanted.
       */
      const isSuperseded =
        action.requestId !== undefined &&
        state.latestRequestId !== undefined &&
        action.requestId !== state.latestRequestId &&
        !action.params?.loadMore &&
        !action.params?.nestedRows

      if (isSuperseded) {
        return state
      }

      if (action.type.endsWith('_FAILURE')) {
        // An appending request never set the loader, so its failure must not clear a
        // replacement load that is still in flight.
        if (action.params?.loadMore || action.params?.nestedRows) {
          return state
        }
        return {
          ...state,
          isLoading: false,
        }
      }
      if (action.type.endsWith('_SUCCESS')) {
        if (action.params?.nestedRows) {
          const { keys } = action.params.nestedRows
          const stateData = state.data as { data?: INestedItem[] }
          return {
            ...state,
            data: {
              ...stateData,
              data: updateNestedData(
                stateData.data || [],
                action.data as unknown as INestedUpdatePayload,
                keys,
                !!action.params.loadMore,
              ),
            } as T,
          }
        }

        // Check if action.data has a 'data' property (list-like structure)
        if (
          action.data &&
          typeof action.data === 'object' &&
          'data' in action.data &&
          Array.isArray(action.data.data)
        ) {
          const stateData = state.data as { data?: unknown[] }
          const newRows = action.data.data as unknown[]
          const isLoadMore = !!action.params?.loadMore
          if (
            isLoadMore &&
            listOptions.pickQuery &&
            !isEqual(listOptions.pickQuery(action.params), state.query)
          ) {
            return state
          }
          return {
            ...state,
            isLoading: false,
            data: {
              // A later page may leave out what only the first carries, such as totals.
              ...(isLoadMore && stateData),
              ...action.data,
              data: isLoadMore ? [...(stateData?.data || []), ...newRows] : newRows,
            },
          }
        }

        // For simple data structures (like two-factor auth)
        return {
          ...state,
          isLoading: false,
          data: action.data,
        }
      }
    }

    if (savingRegExp && savingRegExp.test(action.type)) {
      if (action.type.endsWith('_REQUEST')) {
        return {
          ...state,
          isSaving: true,
          isSaved: false,
        }
      }
      if (action.type.endsWith('_FAILURE') || action.type.endsWith('_SUCCESS')) {
        const baseState = {
          ...state,
          isSaving: false,
          isSaved: action.type.endsWith('_SUCCESS'),
        }
        if (withSaveActionLiveEdit && action.type.endsWith('_SUCCESS')) {
          if (action.params?.customLiveEdit) {
            return action.params.customLiveEdit(baseState, action)
          }
          if (customLiveEdit) {
            return customLiveEdit(baseState, action)
          }

          // Only apply live edit for array-based data structures
          const stateData = state.data as { data?: Array<{ id?: string }> }
          if (stateData && Array.isArray(stateData.data)) {
            const mainAction = action.type.split('/')[1]

            if (mainAction.startsWith('DELETE') || mainAction.startsWith('REMOVE')) {
              const removedIds = action.data.ids || []
              const removedId = action.data.id
              return {
                ...baseState,
                data: {
                  ...stateData,
                  data: stateData.data.filter(
                    (item: { id?: string }) =>
                      item.id !== removedId && !removedIds.includes(item.id || ''),
                  ),
                } as T,
              }
            }
            if (
              mainAction.startsWith('UPDATE') ||
              mainAction.startsWith('RESTORE') ||
              mainAction.startsWith('NOTIFY') ||
              mainAction.startsWith('INVITE') ||
              mainAction.startsWith('ADD_OR_REMOVE')
            ) {
              const items = action.data.items as Array<{ id: string }> | undefined
              if (items) {
                return {
                  ...baseState,
                  data: {
                    ...stateData,
                    data: stateData.data.map((item: { id?: string }) => {
                      const updatedItem = items.find(({ id }) => id === item.id)

                      return updatedItem
                        ? withMergeOnUpdate
                          ? { ...item, ...updatedItem }
                          : updatedItem
                        : item
                    }),
                  } as T,
                }
              }

              const actionData = action.data as { id?: string } & Record<string, unknown>
              return {
                ...baseState,
                data: {
                  ...stateData,
                  data: stateData.data.map((item: { id?: string }) =>
                    item.id === actionData.id
                      ? withMergeOnUpdate
                        ? { ...item, ...actionData }
                        : actionData
                      : item,
                  ),
                } as T,
              }
            }
            if (mainAction.startsWith('CREATE') || mainAction.startsWith('ADD')) {
              if (!stateData.data) {
                return state
              }
              const newItem = action.data as { id?: string }
              if (stateData.data.find((item: { id?: string }) => item.id === newItem.id)) {
                return state
              }
              if (ADD_TO_TOP_ACTIONS.includes(action.type)) {
                return {
                  ...baseState,
                  data: {
                    ...stateData,
                    data: [newItem, ...stateData.data],
                  } as T,
                }
              }
              return {
                ...baseState,
                data: {
                  ...stateData,
                  data: [...stateData.data, newItem],
                } as T,
              }
            }
          }
        }
        return baseState
      }
    }
    if (resetActionTypes && resetActionTypes.includes(action.type)) {
      return initialState
    }

    return state
  }
}
/**
 * Like `createLoadingDataReducer`, but partitions state into a
 * `Record<key, ILoadingData<T>>` bucketed by `action.params[keyParam]`,
 * delegating each action to the same per-key loading/saving logic. Use this
 * when multiple independent consumers dispatch the same request type
 * concurrently with different `params` (e.g. AR vs. AP aging reports) — a
 * plain `createLoadingDataReducer` slot would have the last response to
 * arrive overwrite the others. `keys` pre-seeds every known bucket so
 * consumers destructuring the slot never get `undefined`.
 *
 * IMPORTANT: every action type passed in `loadingActionTypes` /
 * `savingActionTypes` MUST carry `params[keyParam]`, because an action without
 * it has no bucket to route to and is dropped silently (no error, no state
 * change). Conversely, unrelated actions that happen to carry `params[keyParam]`
 * are harmless — the referential-equality check below returns the existing
 * state object, so consumers selecting the whole record don't see a new
 * reference and don't re-render.
 */
export const createKeyedLoadingDataReducer = <T = any, K extends string = string>(
  loadingActionTypes: string[],
  keyParam: string,
  keys: K[],
  savingActionTypes?: string[],
): ((state: Record<K, ILoadingData<T>> | undefined, action: any) => Record<K, ILoadingData<T>>) => {
  const initialState = keys.reduce<Record<K, ILoadingData<T>>>(
    (acc, key): Record<K, ILoadingData<T>> => ({
      ...acc,
      [key]: defaultLoadingDataState<T>(),
    }),
    {} as Record<K, ILoadingData<T>>,
  )
  return createKeyedReducer<ILoadingData<T>, K>(
    createLoadingDataReducer<T>(loadingActionTypes, savingActionTypes),
    keyParam,
    { initialState },
  )
}

interface IKeyedReducerOptions<S, K extends string> {
  initialState?: Record<K, S>
  /** Also hands actions that carry no key to every bucket, for updates that don't name one. */
  fanOutUnkeyed?: boolean
  /** Drops the least recently routed-to bucket once a new one would exceed this. */
  maxKeys?: number
}

/**
 * Runs `inner` once per bucket of a `Record<key, S>`, routing each action by
 * `action.params[keyParam]`. Returns the same record when no bucket changed, so
 * consumers of the whole record don't re-render on unrelated actions that happen
 * to carry the key (e.g. `GET_AR_AP_CHART_DROPDOWN_OPTIONS_*` also passes `agingReport`).
 */
export const createKeyedReducer = <S, K extends string = string>(
  inner: (state: S | undefined, action: any) => S,
  keyParam: string,
  {
    initialState = {} as Record<K, S>,
    fanOutUnkeyed = false,
    maxKeys,
  }: IKeyedReducerOptions<S, K> = {},
) => {
  return (
    state: Record<K, S> = initialState,
    action: Action & { params?: Record<string, unknown> },
  ): Record<K, S> => {
    const key = action.params?.[keyParam] as K | undefined
    if (key) {
      const nextForKey = inner(state[key], action)
      if (nextForKey === state[key]) {
        return state
      }
      if (!maxKeys) {
        return { ...state, [key]: nextForKey }
      }
      // Re-inserting moves the key last, so insertion order is recency order.
      const entries = Object.entries(state).filter(([bucketKey]) => bucketKey !== key)
      return Object.fromEntries([...entries, [key, nextForKey]].slice(-maxKeys)) as Record<K, S>
    }
    if (!fanOutUnkeyed) {
      return state
    }
    let isChanged = false
    const next = {} as Record<K, S>
    for (const [bucketKey, bucket] of Object.entries(state) as [K, S][]) {
      next[bucketKey] = inner(bucket, action)
      isChanged ||= next[bucketKey] !== bucket
    }
    return isChanged ? next : state
  }
}

export function putPromise(action: IPromiseAction) {
  return put(action as any)
}

/**
 * Starts each watcher detached. Under a plain `all`, one worker that throws cancels every
 * other watcher in the app, so follow-up refetches silently stop until the page is reloaded.
 */
export function* runDetached(sagas: Iterator<unknown>[]) {
  yield all(sagas.map((saga) => spawn(() => saga)))
}

type INonFatalHandler<A> = (action: A) => Generator<unknown, void, unknown> | void

/**
 * A socket-fed saga handler made non-fatal: a throw would end the root saga every other saga
 * runs under, so it goes to the Error Center as "<label> failed: …" instead.
 */
export const nonFatal = <A extends Action>(label: string, handle: INonFatalHandler<A>) =>
  function* (action: A) {
    try {
      const running = handle(action)
      if (running) yield* running
    } catch (error) {
      const { message, stack } = error instanceof Error ? error : new Error(String(error))
      yield putPromise(
        logError({
          message: `${label} failed: ${message}`,
          url: window.location.href,
          stack,
        }),
      )
    }
  }
